import React from 'react'
import { useHistory } from 'react-router-dom'
import { ICON_MAP } from './icons'
import CommPanel from './CommPanel'

function formatAge(dateStr) {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d`
}

function formatCurrency(val) {
  if (!val) return ''
  if (val >= 1000000) return '$' + (val / 1000000).toFixed(1) + 'M'
  if (val >= 1000) return '$' + Math.round(val / 1000) + 'K'
  return '$' + val.toFixed(0)
}

function formatBidDate(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24))
  if (diffDays <= 1) return 'Tomorrow'
  if (diffDays <= 7) return date.toLocaleDateString('en-US', { weekday: 'short' })
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function ListItem({ item, children, tsStyle }) {
  const history = useHistory()
  const Ic = ICON_MAP[item.icon]
  const clickable = !!item.linkTo
  const handleClick = () => {
    if (item.linkTo) history.push(item.linkTo)
  }
  return (
    <div
      className={`list-item ${item.type}`}
      onClick={clickable ? handleClick : undefined}
      style={clickable ? { cursor: 'pointer' } : undefined}
    >
      <div className="ic">{Ic && <Ic size={14} />}</div>
      <div className="item-text">
        {children}
      </div>
      <span className="ts" style={tsStyle}>
        {typeof item.ts === 'string' && !item.ts.includes('T') ? item.ts : formatAge(item.ts)}
      </span>
    </div>
  )
}

function NeedsAttention({ items = [], estimatingItems = [] }) {
  const totalCount = items.length + estimatingItems.length
  return (
    <div className="panel">
      <div className="panel-head">
        <h4>Needs Attention</h4>
        <div className="right">
          {totalCount > 0 && (
            <span className="pill pill-risk" style={{ fontSize: '9.5px' }}>
              <span className="dot" />{totalCount} item{totalCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      <div>
        {totalCount === 0 && (
          <div style={{ padding: '16px 14px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
            Nothing needs attention right now
          </div>
        )}
        {items.map((item, i) => (
          <ListItem key={i} item={item}>
            <div className="item-label">{item.label}</div>
            <div className="item-desc">{item.desc}</div>
          </ListItem>
        ))}
        {estimatingItems.length > 0 && (
          <>
            <div style={{ padding: '6px 14px 2px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-4)' }}>
              Estimating
            </div>
            {estimatingItems.map((item, i) => (
              <ListItem key={`est-${i}`} item={item}>
                <div className="item-label">{item.label}</div>
                <div className="item-desc">{item.desc}</div>
              </ListItem>
            ))}
          </>
        )}
      </div>
    </div>
  )
}

function RecentActivity({ items = [] }) {
  return (
    <div className="panel">
      <div className="panel-head">
        <h4>Recent Activity</h4>
        <div className="right">
          <button className="dbtn dbtn-ghost" style={{ height: 24, fontSize: '11.5px' }}>View all</button>
        </div>
      </div>
      <div>
        {items.length === 0 && (
          <div style={{ padding: '16px 14px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
            No recent activity
          </div>
        )}
        {items.map((item, i) => (
          <ListItem key={i} item={item}>
            <div className="item-label">{item.label}</div>
            <div className="item-desc">
              {item.desc}
              {item.actor && !item.desc?.includes(item.actor) && (
                <span style={{ color: 'var(--ink-4)' }}> &middot; {item.actor}</span>
              )}
            </div>
          </ListItem>
        ))}
      </div>
    </div>
  )
}

function UpcomingBids({ items = [] }) {
  const history = useHistory()
  const statusLabels = { draft: 'Draft', estimating: 'Estimating', review: 'In Review' }
  const statusTones = { draft: 'warn', estimating: 'info', review: 'brand' }

  return (
    <div className="panel">
      <div className="panel-head">
        <h4>Upcoming Bid Deadlines</h4>
      </div>
      <div>
        {items.length === 0 && (
          <div style={{ padding: '16px 14px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
            No upcoming deadlines
          </div>
        )}
        {items.map((bid) => {
          const dueDate = new Date(bid.bidDueDate)
          const now = new Date()
          const diffDays = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24))
          const isDueTomorrow = diffDays <= 1
          const isDueThisWeek = diffDays <= 7
          const listType = isDueTomorrow ? 'alert' : isDueThisWeek ? 'warn' : 'info'

          return (
            <div
              className={`list-item ${listType}`}
              key={bid._id}
              onClick={() => history.push(`/bid-estimation/${bid._id}`)}
              style={{ cursor: 'pointer' }}
            >
              <div className="ic">
                {ICON_MAP.clock && <ICON_MAP.clock size={14} />}
              </div>
              <div className="item-text">
                <div className="item-label">{bid.name}</div>
                <div className="item-desc" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {bid.totalSell ? formatCurrency(bid.totalSell) : bid.totalCost ? formatCurrency(bid.totalCost) : ''}
                  {bid.client && <span>&middot; {bid.client}</span>}
                  <span className={`pill pill-${statusTones[bid.status] || 'neutral'}`} style={{ fontSize: 9 }}>
                    <span className="dot" />{statusLabels[bid.status] || bid.status}
                  </span>
                </div>
              </div>
              <span className="ts" style={isDueTomorrow ? { color: 'var(--risk)', fontWeight: 600 } : undefined}>
                {formatBidDate(bid.bidDueDate)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function SidebarPanels({ attentionItems = [], activityItems = [], bidDeadlines = [], estimatingAttention = [], commData, onOpenCommDrawer }) {
  return (
    <div className="sidebar-stack">
      <NeedsAttention items={attentionItems} estimatingItems={estimatingAttention} />
      <RecentActivity items={activityItems} />
      <UpcomingBids items={bidDeadlines} />
      <CommPanel commData={commData} onOpenDrawer={onOpenCommDrawer} />
    </div>
  )
}
